import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { updateNotification } from 'containers/Login/loginSlice'
import { checkIsNativeApp } from 'utils/nativeApp'
import { initializeApp } from 'firebase/app'
import { DELETE, POST } from 'rest/request'
import { useReduxDispatch } from 'store'
import FCMContext from './Context'
import * as urls from 'rest/urls'
import React from 'react'

interface FirebaseProviderProps {
  children: React.ReactNode
}

const isNativeApp = checkIsNativeApp()
const vapidKey = process.env.REACT_APP_VAPID_KEY
let messaging: ReturnType<typeof getMessaging> | undefined

if (
  !isNativeApp &&
  'serviceWorker' in navigator &&
  process.env.REACT_APP_FIREBASE_API_KEY &&
  process.env.REACT_APP_FIREBASE_PROJECT_ID &&
  process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID &&
  process.env.REACT_APP_FIREBASE_APP_ID
) {

  const app = initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseapp.com`,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.firebasestorage.app`,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
  })

  messaging = getMessaging(app)

  onMessage(messaging, (payload) => {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        payload: payload
      })
    }
  })
}

const isFCMEnabled = !!(vapidKey && messaging && !isNativeApp)

const FirebaseProvider: React.FC<FirebaseProviderProps> = ({ children }) => {
  const dispatch = useReduxDispatch()
  const registerFCM = async () => {
    if (!isFCMEnabled) return
    try {
      dispatch(updateNotification(undefined))
      const serviceWorkerRegistration = await navigator.serviceWorker.ready
      const token = await getToken(messaging!, { vapidKey, serviceWorkerRegistration })

      if (token) {
        const body = {
          provider: 'firebase',
          token
        }
        const response = await POST(urls.webpushdevices, { body })
        if (response) {
          dispatch(updateNotification(true))

        } else {
          throw new Error('Sending FCM to token failed!')
        }

      } else {
        throw new Error('There is no FCM token!')
      }

    } catch (error) {
      console.error('Error registerFCM:', error)
      dispatch(updateNotification(false))
    }
  }

  const unregisterFCM = async () => {
    if (!isFCMEnabled) return
    dispatch(updateNotification(undefined))
    const response = await DELETE(urls.webpushdevices)
    if (response) {
      dispatch(updateNotification(false))

    } else {
      console.warn('Sending FCM to token failed!')
      dispatch(updateNotification(true))
    }
  }

  return (
    <FCMContext.Provider value={{ registerFCM, unregisterFCM }}>
      {children}
    </FCMContext.Provider>
  )
}

export default FirebaseProvider